import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { SlidersHorizontal, Search, X } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { PropertyCard } from "@/components/PropertyCard";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";

const types = ["Tutte", "Appartamento", "Villa", "Attico", "Terratetto", "Villetta a schiera", "Commerciale", "Terreno"];
const contracts = ["Tutti", "Vendita", "Affitto"];
const zones = ["Tutte", "Scandicci Centro", "Casellina", "Vingone", "Le Bagnese", "Firenze Isolotto", "Firenze Centro", "Firenze San Frediano", "Colline / Chianti", "Altro"];

export default function Immobili() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [type, setType] = useState("Tutte");
  const [contract, setContract] = useState("Tutti");
  const [zone, setZone] = useState("Tutte");
  const [maxPrice, setMaxPrice] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "Immobili in vendita e affitto a Scandicci e Firenze | Edilstudio",
    description: "Sfoglia gli immobili disponibili a Scandicci e Firenze: appartamenti, ville, attici. Filtri per zona, prezzo e tipologia. Conformità verificata dal nostro studio tecnico.",
    canonicalPath: "/immobili",
  });

  const queryParams = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (type !== "Tutte") p.type = type;
    if (contract !== "Tutti") p.contract = contract;
    if (zone !== "Tutte") p.zone = zone;
    if (maxPrice) p.max_price = maxPrice;
    return p;
  }, [q, type, contract, zone, maxPrice]);

  const { data: properties = [], isLoading } = useQuery({
    queryKey: ["properties", queryParams],
    queryFn: async () => (await api.get("/properties", { params: queryParams })).data,
  });

  const sortedProperties = useMemo(() => {
    const isUnav = (p) => ["Venduto", "Affittato", "Non più disponibile"].includes(p.status);
    return [...properties].sort((a, b) => (isUnav(a) ? 1 : 0) - (isUnav(b) ? 1 : 0));
  }, [properties]);

  useEffect(() => { setParams(q ? { q } : {}); }, [q]);

  const reset = () => { setQ(""); setType("Tutte"); setContract("Tutti"); setZone("Tutte"); setMaxPrice(""); };

  const sel = "px-4 py-2.5 rounded-xl border border-[#DCE6EF] bg-white text-[#1B2A41] focus:outline-none focus:ring-2 focus:ring-[#2472C8]/40";

  return (
    <PublicLayout>
      <div className="bg-[#1B2A41] py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="eyebrow text-[#F5A623]">Portale immobili</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mt-2">Immobili a Scandicci e Firenze</h1>
          <div className="relative mt-8 max-w-2xl">
            <Search className="w-5 h-5 absolute left-4 top-4 text-[#8A97A6]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per zona, tipologia o parola chiave…" className="w-full pl-12 pr-4 py-4 rounded-xl bg-white text-[#1B2A41] focus:outline-none" data-testid="immobili-search" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setShowFilters(!showFilters)} className="lg:hidden flex items-center gap-2 bg-white border border-[#DCE6EF] px-4 py-2.5 rounded-xl font-medium" data-testid="toggle-filters">
            <SlidersHorizontal className="w-4 h-4" /> Filtri
          </button>
          <span className="text-sm text-[#566476] font-mono" data-testid="results-count">{properties.length} {properties.length === 1 ? "immobile trovato" : "immobili trovati"}</span>
        </div>

        <div className={`grid lg:grid-cols-4 gap-3 mb-8 ${showFilters ? "grid" : "hidden lg:grid"}`} data-testid="filters-bar">
          <select value={type} onChange={(e) => setType(e.target.value)} className={sel} data-testid="filter-type">{types.map((t) => <option key={t}>{t}</option>)}</select>
          <select value={contract} onChange={(e) => setContract(e.target.value)} className={sel} data-testid="filter-contract">{contracts.map((t) => <option key={t}>{t}</option>)}</select>
          <select value={zone} onChange={(e) => setZone(e.target.value)} className={sel} data-testid="filter-zone">{zones.map((t) => <option key={t}>{t}</option>)}</select>
          <div className="flex gap-2">
            <input value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Prezzo max €" className={`${sel} flex-1`} data-testid="filter-maxprice" />
            <button onClick={reset} className="px-3 rounded-xl border border-[#DCE6EF] bg-white text-[#566476] hover:text-[#2472C8]" title="Azzera filtri" data-testid="reset-filters"><X className="w-4 h-4" /></button>
          </div>
        </div>

        {isLoading ? (
          <div className="py-20 text-center text-[#8695A6] font-mono">Caricamento immobili…</div>
        ) : properties.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="properties-grid">
            {sortedProperties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        ) : (
          <div className="py-20 bg-white rounded-2xl border border-dashed border-[#CBD7E2] text-center" data-testid="no-results">
            <p className="text-[#1B2A41] font-semibold text-lg">Nessun immobile corrisponde ai filtri</p>
            <p className="text-[#8695A6] mt-2">Prova a modificare i criteri di ricerca o contattaci: molti immobili sono trattati in anteprima.</p>
            <button onClick={reset} className="mt-5 bg-[#2472C8] text-white font-semibold px-5 py-2.5 rounded-full">Azzera filtri</button>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
